import api from '../../api';
import {getDateRangesApiModel, userIdPath} from '../../helpers/apiHelper';
import {
    availabilityResponse,
    type AvailabilityResponse,
} from '../../models/booking/bookingDateAvailability';
import {
    resourceUsageListModel,
    type ResourceUsageListModel,
    resourceUsageModel,
    type ResourceUsageModel,
} from '../../models/booking/bookingModel';
import {
    resource,
    type Resource,
    resourceDetails,
    type ResourceDetails,
    resourceType,
    type ResourceType,
    type ResourceTypeFilter,
} from '../../models/booking/resourceModel';
import {resourceLiveStatus, type ResourceLiveStatus} from '../../models/booking/resourceLiveStatus';
import type {DateRange} from '../../models/dateTime/dateRange';
import {
    mercureSubscription,
    type MercureSubscription,
} from '../../models/mercure/mercureSubscription';
import {parseZodSchema} from '../../models/parseZodSchema';

export async function getResourceTypes(): Promise<ResourceType[]> {
    return api
        .get('/api/resource-type/list')
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, resourceType.array()));
}

export async function getMapResources(
    roomId?: string | null,
    userId?: string
): Promise<Resource[]> {
    return api
        .get(`/api/resource/list/${roomId}${userIdPath(userId)}`)
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, resource.array()));
}
export async function getAdminMapResources(roomId?: string | null): Promise<Resource[]> {
    return api
        .get(`/api/admin/resource/list/${roomId}`)
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, resource.array()));
}

export async function getListResources(
    spaceId: string | null,
    dateRanges: DateRange[],
    resourceTypeFilter: ResourceTypeFilter[] | null,
    userId?: string,
    showOnlyBookable: boolean = false,
    showOnlyFavorites: boolean = false
): Promise<ResourceUsageListModel[]> {
    const request = {
        filter: {
            showOnlyBookable,
            showOnlyFavorites,
            resourceTypeFilter,
        },
        ...getDateRangesApiModel(dateRanges),
    };

    return api
        .postJSON(
            `/api/resource/usage/list/${spaceId}${userIdPath(userId)}`,
            Object.assign(request)
        )
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, resourceUsageListModel.array()));
}

export function getBookingState(
    resourceId: string,
    dateRanges: readonly DateRange[],
    userId?: string
): Promise<AvailabilityResponse> {
    return api
        .postJSON(
            `/api/resource/availability/${resourceId}${userIdPath(userId)}`,
            getDateRangesApiModel(dateRanges)
        )
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, availabilityResponse));
}

export async function getResourceUsages(
    roomId: string,
    dateRanges: readonly DateRange[],
    userId?: string
): Promise<ResourceUsageModel[]> {
    return api
        .postJSON(
            `/api/resource/room/usage/list/${roomId}${userIdPath(userId)}`,
            getDateRangesApiModel(dateRanges)
        )
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, resourceUsageModel.array()));
}

export async function getResourceDetails(resourceId: string): Promise<ResourceDetails> {
    return api
        .get(`/api/resource/${resourceId}`)
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, resourceDetails));
}

export async function getRoomResourcesLiveStatus(roomId: string): Promise<ResourceLiveStatus[]> {
    return api
        .get(`/api/resource/room/live/status/${roomId}`)
        .then(api.handle)
        .catch(api.error)
        .then(raw => parseZodSchema(raw, resourceLiveStatus.array()));
}

export async function getRoomLiveStatusSubscription(
    roomId: string
): Promise<MercureSubscription | null> {
    return api
        .get(`/api/resource/room/live/status/${roomId}/subscribe`)
        .then(api.handle)
        .then(raw => parseZodSchema(raw, mercureSubscription))
        .catch(() => null); // Silent failure - graceful degradation to polling
}
